import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import Styles from 'screens/BrandKits/components/BrandVoice/AddVoice/AddVoice.module.scss'
import { Step1 } from 'screens/BrandKits/components/BrandVoice/AddVoice/Step1'
import { Button } from 'blaze-ui/Button'
import { useMemo, useState } from 'react'
import { Importer } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/ContentSourcePicker'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { AddVoiceModalProps } from './Types'
import { usePreventScroll } from '@react-aria/overlays'

export type OnAnalyse = ({
  content,
  selectedImporter,
  onAnalyse,
}: {
  content: string
  selectedImporter: Importer
  onAnalyse: OnAnalyse
}) => void

export interface AnalyseContentModalProps extends AddVoiceModalProps, Pick<StackModalProps, 'close'> {
  onAnalyse: OnAnalyse
  selectedImporter?: Importer
  availableImporters?: Importer[]
  errorMessage?: string
}

export const AnalyseContentModal = ({
  close,
  onComplete,
  onAnalyse,
  availableImporters,
  selectedImporter: initialSelectedImporter,
  errorMessage,
}: AnalyseContentModalProps) => {
  usePreventScroll()
  const [content, setContent] = useState<string>('')
  const [selectedImporter, setSelectedImporter] = useState<Importer>(initialSelectedImporter || Importer.ScanWebsite)
  const { closeModal: closeAlmanacModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  const isNextStepDisabled = useMemo(() => {
    switch (selectedImporter) {
      case Importer.ScanWebsite:
        return isUrlCrawlable(content).isValid === false || content.trim() === ''
      case Importer.AddText:
      case Importer.PasteWebsiteText:
      case Importer.SelectDoc:
      case Importer.UploadFiles:
        return content.trim().length < 10
    }
  }, [selectedImporter, content])

  const closeModal = (voiceId?: number) => {
    onComplete?.(voiceId)

    close()
  }

  return (
    <Modal.Root
      size="sm"
      blaze
      withDefaultBg={isMobile}
      onClose={closeModal}
      onPressOutside={closeModal}
      aria-label="Analyze Content"
      bodyClassName={Styles.modalBody}
    >
      <Step1
        selectedImporter={selectedImporter}
        sampleText={content}
        setSampleText={setContent}
        onClose={closeModal}
        setSelectedImporter={setSelectedImporter}
        availableImporters={availableImporters}
        errorMessage={errorMessage}
      />
      <Modal.Content blaze>
        <div key="step1placeholder" />
      </Modal.Content>
      <Modal.Footer blaze sticky={isMobile} blur={isMobile} withContentBackground={true}>
        <Modal.FooterContent slot="left">
          <Button onPress={closeAlmanacModal} size="lg" variant="tertiary">
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.buttonsRow}>
            <Button
              size="lg"
              isDisabled={isNextStepDisabled}
              onPress={async () => {
                if (content !== '') {
                  onAnalyse({ content, selectedImporter, onAnalyse })
                }
              }}
            >
              Analyze for Voice
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
